import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveOption } from '../lib/agent/options';
import { navigationRefs } from '../api/agent/navigation';

/**
 * Page state that lives in the URL's query string.
 *
 * A tab or control held in `useState` cannot be reached from outside the page:
 * not by a link, not by the back button, not by the agent. Held in the URL, the
 * agent sets it the same way a link does — by navigating with `?tab=risk` — and
 * the page simply renders what the URL says. That also fixes the failure Ki_UI's
 * `useAgentTab` had: asking again for a tab the user had since left did nothing,
 * because the effect only fired when the requested value changed. There is no
 * effect here; the URL is read on every render and is the only state.
 */

const listOf = (raw) => String(raw).split(',').map((s) => s.trim()).filter(Boolean);

/** Stable string for a fallback or an option list, so memoised values keep their identity. */
const keyOf = (v) => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map((o) => (o && typeof o === 'object' ? o.id : o)).join('\u0001');
  return String(v);
};

/** URL text → typed value. Unset, empty, unparseable or unrecognised all read as the fallback. */
function parseValue(raw, { options, fallback, type }) {
  if (raw == null || raw === '') return fallback;
  const hasOptions = Array.isArray(options) && options.length > 0;
  if (type === 'list') {
    const items = listOf(raw)
      .map((item) => (hasOptions ? resolveOption(item, options) : item))
      .filter((item) => item != null && item !== '');
    const unique = [...new Set(items)];
    return unique.length ? unique : fallback;
  }
  const text = hasOptions ? resolveOption(raw, options) : raw;
  if (text == null) return fallback;
  if (type === 'number') {
    const n = Number(text);
    return Number.isFinite(n) ? n : fallback;
  }
  return text;
}

/** Typed value → URL text, or null when the param should be removed. */
function serialiseValue(value, { options, fallback, type }) {
  if (value == null || value === '') return null;
  const hasOptions = Array.isArray(options) && options.length > 0;
  const canonical = (v) => (hasOptions ? (resolveOption(v, options) ?? v) : v);
  let text;
  if (type === 'list') {
    const items = (Array.isArray(value) ? value : listOf(value)).map((v) => String(canonical(v)));
    if (!items.length) return null;
    text = items.join(',');
    const fb = Array.isArray(fallback) ? fallback.map(String).join(',') : fallback;
    if (fb != null && text === String(fb)) return null;
    return text;
  }
  if (type === 'number') {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    text = String(n);
  } else {
    text = String(canonical(value));
  }
  if (fallback != null && text === String(fallback)) return null;
  return text;
}

/**
 * The last search string a setter wrote that React has not rendered yet.
 *
 * react-router's own functional `setSearchParams` hands the updater the params
 * of the last RENDER, so two setters called in one handler (a tab and a cadence
 * together) each start from the old URL and the second silently undoes the
 * first. Every setter here builds on the pending write instead while the
 * rendered location is still the one it was built from. Module-level so that
 * two different hooks on the same page share it; cleared on the next task, by
 * which point the router has rendered the write.
 */
let pendingWrite = null;

function baseSearch(rendered) {
  if (pendingWrite && pendingWrite.pathname === rendered.pathname && pendingWrite.from === rendered.search) {
    return pendingWrite.to;
  }
  return rendered.search;
}

/**
 * One query param as state: `[value, setValue]`.
 *
 * - `options` (`[{ id, label }]` or ids): the raw value is resolved against
 *   them, so `?tab=Risk` reads as `risk` and an unknown value reads as the
 *   fallback.
 * - `fallback`: what an absent or unusable param reads as. Setting the fallback
 *   removes the param, so a default view keeps a clean URL.
 * - `replace` (default true): a tab switch replaces the history entry rather
 *   than making Back step through every tab.
 * - `type`: `'string'` (default), `'number'`, or `'list'` (comma-separated ↔
 *   array of strings).
 *
 * `setValue` is stable for the life of the component, accepts a value or an
 * updater `(current) => next` like `useState`'s, keeps every other param, and
 * does nothing when the URL would not change — so calling it from an effect
 * cannot loop.
 *
 * It also does nothing once its page is gone: after the component unmounted,
 * or when the router already shows another path. A tab in `useState` ignored a
 * late setter; this one navigates, and react-router's `navigate` keeps working
 * after unmount. An agent handler that finishes after the reader left the page
 * (a confirmation card stays in the dock across screens) would otherwise pull
 * them back to it and overwrite the history entry of the page they chose.
 */
export function useUrlParam(name, {
  options, fallback = null, replace = true, type = 'string',
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();

  const raw = useMemo(() => new URLSearchParams(location.search).get(name), [location.search, name]);

  /* Keyed on the option ids and the fallback's text rather than their identity:
     callers pass literals, and a list value (or a list fallback) whose identity
     changed every render would re-run any effect that depends on it. */
  const optionsKey = keyOf(options);
  const fallbackKey = Array.isArray(fallback) ? `[${keyOf(fallback)}]` : keyOf(fallback);
  const config = { options, fallback, type };
  const configRef = useRef(config);
  configRef.current = config;
  const value = useMemo(
    () => parseValue(raw, configRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the keys stand in for options and fallback, see above
    [raw, optionsKey, fallbackKey, type],
  );

  /* The setter reads everything through refs so its identity never changes;
     `navigate` in particular changes on every location change. */
  const live = useRef(null);
  live.current = {
    name, replace, navigate, location, value,
  };
  /* Set in an effect, so StrictMode's unmount and remount leave it true. */
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const setValue = useCallback((next) => {
    if (!mounted.current) return;
    const {
      name: param, replace: replaceEntry, navigate: go, location: rendered, value: currentValue,
    } = live.current;
    /* The page is still mounted but the route has already moved on (the same
       tick as a navigation): the URL to write belongs to a page no longer shown. */
    const shown = navigationRefs.location;
    if (shown && typeof shown.pathname === 'string' && shown.pathname !== rendered.pathname) return;
    const from = baseSearch(rendered);
    /* An updater sees a write from earlier in the same handler, as useState's does. */
    const latest = from === rendered.search
      ? currentValue
      : parseValue(new URLSearchParams(from).get(param), configRef.current);
    const resolved = typeof next === 'function' ? next(latest) : next;
    const text = serialiseValue(resolved, configRef.current);

    const params = new URLSearchParams(from);
    if (text == null) params.delete(param);
    else params.set(param, text);
    const search = params.toString();
    /* Both sides re-encoded by URLSearchParams, so `a,b` typed into the address
       bar and `a%2Cb` written here compare equal and no entry is pushed. */
    if (search === new URLSearchParams(from).toString()) return;
    /* Commas are legal in a query string and a list reads far better unescaped. */
    const target = search ? `?${search.replace(/%2C/gi, ',')}` : '';

    const write = { pathname: rendered.pathname, from: rendered.search, to: target };
    pendingWrite = write;
    setTimeout(() => {
      if (pendingWrite === write) pendingWrite = null;
    }, 0);
    go({ pathname: rendered.pathname, search: target, hash: rendered.hash }, { replace: replaceEntry });
  }, []);

  return [value, setValue];
}

/**
 * The page's tab, read from `?tab=` (or `param`) against the page's own tabs.
 *
 * `options` is the list the page renders (`[{ id, label }]`), so labels stay
 * with the page; the agent's catalogue lists the same ids in the screen's
 * `tab_control`, and `scripts/check-ui-discovery.mjs` checks the two agree.
 * An agent asking for "Risk" or `risk` lands on the same tab, and anything no
 * tab matches reads as `fallback` — by default the first tab.
 */
export function useUrlTab(options, { param = 'tab', fallback } = {}) {
  const list = Array.isArray(options) ? options : [];
  const effectiveFallback = fallback !== undefined ? fallback : (list[0]?.id ?? null);
  /* No options means nothing to resolve against: the raw value is used as-is
     rather than every tab reading as the fallback forever. */
  return useUrlParam(param, {
    options: list.length ? list : undefined,
    fallback: effectiveFallback,
  });
}
